import React, { useState } from 'react'
import { BsHeart, BsHeartFill } from 'react-icons/bs'
import { CgPen } from 'react-icons/cg'

import { Spinner } from 'common/components/Spinner/Spinner'
import Tag from 'common/components/Tag/Tag'

import {
  useListenBannedMembersQuery,
  useListenMyCommunityLikesQuery,
  useListenPostRepliesQuery,
  useToggleCommunityReplyLikeMutation,
} from '../communityApi'
import { filterVisiblePosts, normalizeCommunityRecords } from '../communityUtils'
import { hasReplyAttachment } from '../postButtonUtils'
import useCoachAuthor from '../useCoachAuthor'
import formatFeedTime from './formatFeedTime'
import PostAttachmentCard, { usePostAttachment } from './PostAttachmentCard'
import ReplyComposer from './ReplyComposer'
import PostAvatar from './PostAvatar'
import { BanMemberButton, DeleteReplyButton, MemberName } from './MemberActions'

// The expanded thread under a post row: replies oldest-first, member replies the
// coach hasn't seen yet highlighted, and the reply box (unless replies are off).
export default function PostThread({ post, coachOrgId, userId }) {
  const { data: repliesData } = useListenPostRepliesQuery({ coachOrgId, postId: post.id })
  const repliesLoading = repliesData === undefined || repliesData?.isLoading
  // Replies from banned members are hidden, as on the app (the records stay
  // in the database so an unban restores them).
  const { data: bannedMembersData } = useListenBannedMembersQuery({ coachOrgId })
  const bannedMemberIds = bannedMembersData?.isLoading || !bannedMembersData ? {} : bannedMembersData
  const replies = filterVisiblePosts(
    Object.values(normalizeCommunityRecords(repliesLoading ? {} : repliesData)),
    {},
    bannedMemberIds
  ).sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))
  const coachLastSawAt = post?.lastCoachActivityAt || 0

  const author = useCoachAuthor({ coachOrgId, userId })
  const { data: myLikesData } = useListenMyCommunityLikesQuery({ coachOrgId, userId })
  const myLikes = myLikesData?.isLoading ? {} : myLikesData || {}
  const [toggleReplyLike] = useToggleCommunityReplyLikeMutation()
  // One reply is edited at a time, in place of its text.
  const [editingReplyId, setEditingReplyId] = useState(null)

  return (
    <div className='border-t border-gray-100 px-6 py-4'>
      {repliesLoading ? (
        <Spinner className='w-5 h-5 text-gray-300 my-2' />
      ) : replies.length === 0 ? (
        <p className='text-sm text-gray-500'>No replies yet.</p>
      ) : (
        <div className='space-y-4'>
          {replies.map((reply) => {
            const isNew = reply.authorIsCoach !== true && (reply.createdAt || 0) > coachLastSawAt
            const isLiked = Boolean(myLikes?.replies?.[post.id]?.[reply.id])
            const isEditing = editingReplyId === reply.id
            return (
              <div key={reply.id} data-testid='community-reply' className='relative group/reply'>
                {/* Avatar and the name/time block center against each other;
                    the text below indents to align with the name column. */}
                <div className='flex items-center'>
                  <PostAvatar
                    name={reply.authorName}
                    picture={reply.authorPicture}
                    color={reply.authorColor}
                    size={32}
                  />
                  <div className='ml-2 min-w-0'>
                    <div className='flex items-center'>
                      <MemberName
                        coachOrgId={coachOrgId}
                        userId={reply.authorId}
                        name={reply.authorName}
                        isCoach={reply.authorIsCoach === true}
                        className='font-semibold text-sm text-tBlack truncate'
                      />
                      {reply.authorIsCoach && <Tag text='Coach' className='!mb-0 !text-xs !rounded-md ml-2' />}
                      {isNew && <Tag text='New' className='!mb-0 !text-xs !rounded-md ml-2 !bg-tGreen !text-white' />}
                    </div>
                    <div className='text-xs text-gray-500 -mt-0.5'>
                      {formatFeedTime(reply.createdAt)}
                      {reply.editedAt ? ' (edited)' : ''}
                    </div>
                  </div>
                </div>
                <div className='ml-10 min-w-0'>
                  {isEditing ? (
                    <ReplyComposer
                      post={post}
                      coachOrgId={coachOrgId}
                      userId={userId}
                      reply={reply}
                      onDone={() => setEditingReplyId(null)}
                    />
                  ) : (
                    <>
                      <div className='text-sm text-tGray-dark whitespace-pre-wrap break-words mt-2'>{reply.text}</div>
                      {hasReplyAttachment(reply) && <ReplyAttachment reply={reply} coachOrgId={coachOrgId} />}
                    </>
                  )}
                  <button
                    className='inline-flex items-center text-xs text-gray-500 hover:text-tRed transition-colors mt-2'
                    onClick={() =>
                      toggleReplyLike({
                        coachOrgId,
                        postId: post.id,
                        replyId: reply.id,
                        replyAuthorId: reply.authorId,
                        userId,
                        actorName: author.displayName,
                        isLiked,
                      })
                    }
                    title={isLiked ? 'Unlike' : 'Like'}
                  >
                    {isLiked ? <BsHeartFill className='w-3 h-3 text-tRed' /> : <BsHeart className='w-3 h-3' />}
                    {reply.likeCount > 0 && <span className='ml-1'>{reply.likeCount}</span>}
                  </button>
                </div>
                {/* Same hover bar as the post row, scoped to this reply. A coach can
                    delete any reply, edit only their own, and ban a member (never a
                    coach) from theirs. */}
                <div className='absolute top-0 right-0 inline-flex items-center justify-end bg-white group-hover/reply:border group-hover/reply:border-tBlack group-hover/reply:border-opacity-20 rounded-lg group-hover/reply:shadow-sm'>
                  {reply.authorId === userId && !isEditing && (
                    <button
                      type='button'
                      title='Edit reply'
                      onClick={() => setEditingReplyId(reply.id)}
                      className='hidden group-hover/reply:inline-block hover:text-tGreen py-1 px-2 border-r border-tBlack border-opacity-20 transition-all'
                    >
                      <CgPen className='w-[18px] h-[18px]' />
                    </button>
                  )}
                  {reply.authorId !== userId && reply.authorIsCoach !== true && (
                    <BanMemberButton
                      coachOrgId={coachOrgId}
                      userId={reply.authorId}
                      name={reply.authorName}
                      className='hidden group-hover/reply:inline-block hover:text-tRed py-1 px-2 border-r border-tBlack border-opacity-20 transition-all'
                    />
                  )}
                  <DeleteReplyButton
                    coachOrgId={coachOrgId}
                    postId={post.id}
                    replyId={reply.id}
                    className='hidden group-hover/reply:inline-block hover:text-tRed py-1 px-2 transition-all'
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}
      {post?.repliesOff === true ? (
        <p className='text-xs text-gray-500 mt-3'>Replies are turned off for this post.</p>
      ) : (
        <ReplyComposer post={post} coachOrgId={coachOrgId} userId={userId} />
      )}
    </div>
  )
}

function ReplyAttachment({ reply, coachOrgId }) {
  const attachment = usePostAttachment(reply, coachOrgId)
  return <PostAttachmentCard attachment={attachment} />
}
